<!-- A card, not a link. The whole card used to be one <a> wrapping the photo
     arrows, favourite, map, "+N amenities" and the actions, and a control
     nested inside a link is broken for keyboard and screen-reader users.
     Stretched-link pattern: the title is the only link to the listing, and its
     ::after covers the card (positioned against this relative article), so a
     click anywhere still opens it. Photo controls sit above that layer via
     their existing z-20; body controls get `relative`, which puts them above
     it by DOM order. No z-index values changed. -->
<article
  class="listing-card group relative h-full cursor-pointer touch-pan-y flex flex-col bg-white rounded-2xl overflow-hidden border border-dark-text/8 hover:border-trinidad/40 hover:shadow-[0_12px_32px_rgba(0,0,0,0.08)] transition-all duration-300 no-underline not-italic">

  <!-- Touch swipe on the photo changes photos. Listeners live on the card (the
       title's stretched link covers the photo, so taps land on it) and the card is
       touch-action: pan-y, so vertical page scroll still works while horizontal
       swipes reach the pointer handlers. See ListingCardComponent.ngAfterViewInit. -->
  <div #photoArea class="relative aspect-[4/3] overflow-hidden">
    <!-- NgOptimizedImage forbids changing `priority` on an already-initialized
         image (NG02953) — search-results.component.html sets [priority]="i
         === 0", and @for's track-by-id reuses this card's instance across a
         re-sort, so a listing that becomes/stops being first must get a new
         image element, not a mutated one. Splitting on an @if (Angular's own
         suggested fix for this) forces that recreation. -->
    @if (priority) {
      <img [ngSrc]="photos[currentImageIndex]" [alt]="listing.title"
        width="1600" height="1000"
        [priority]="true"
        decoding="async"
        class="w-full h-full object-cover transition-transform duration-700 group-hover:scale-[1.04]">
    } @else {
      <img [ngSrc]="photos[currentImageIndex]" [alt]="listing.title"
        width="1600" height="1000"
        [priority]="false"
        decoding="async"
        class="w-full h-full object-cover transition-transform duration-700 group-hover:scale-[1.04]">
    }

    <!-- carousel chevrons (hover-reveal, only if >1 photo). Centered within
         a trimmed vertical band (below the heart/map row, above the price
         chip/dots row) so they never overlap the right-edge buttons. -->
    @if (photos.length > 1) {
      <div class="absolute inset-x-0 z-20 flex items-center justify-between px-3 pointer-events-none" style="top: 3.75rem; bottom: 3.25rem;">
        <button type="button" (click)="prevImage($event)"
          aria-label="Previous photo"
          class="pointer-events-auto w-10 h-10 rounded-full bg-white/95 backdrop-blur-sm shadow-md flex items-center justify-center opacity-100 md:opacity-0 md:group-hover:opacity-100 hover:scale-110 transition-all duration-200">
          <span aria-hidden="true" class="material-symbols-outlined text-[18px] text-dark-text">chevron_left</span>
        </button>
        <button type="button" (click)="nextImage($event)"
          aria-label="Next photo"
          class="pointer-events-auto w-10 h-10 rounded-full bg-white/95 backdrop-blur-sm shadow-md flex items-center justify-center opacity-100 md:opacity-0 md:group-hover:opacity-100 hover:scale-110 transition-all duration-200">
          <span aria-hidden="true" class="material-symbols-outlined text-[18px] text-dark-text">chevron_right</span>
        </button>
      </div>

      <!-- Dot indicators. The visible mark stays 8px; the padding is the tap area.
           It was `p-2`, i.e. 24x44 — under the 32px floor on width, on the most
           repeated control of the primary browsing surface, sitting on top of a
           photo where a mis-tap opens the listing. `px-3` makes it 32x44.
           Deliberately not the full 44px guideline: five adjacent 44px targets
           would span 220px on a ~343px card photo and touch each other, so a
           mis-tap would reliably select the wrong photo instead of the wrong
           page — trading one error for another rather than removing it. -->
      <div class="absolute bottom-2 left-1/2 -translate-x-1/2 z-20 flex items-center">
        @for (p of photos; track $index) {
          <button type="button" (click)="goToImage($index, $event)"
            [attr.aria-label]="'Go to photo ' + ($index + 1)"
            [attr.aria-current]="$index === currentImageIndex ? 'true' : null"
            class="px-3 py-[18px] -my-[18px] group/dot"
            style="-webkit-tap-highlight-color: transparent;">
            <span
              class="block w-2 h-2 rounded-full transition-all duration-200"
              [ngClass]="$index === currentImageIndex ? 'bg-white scale-125' : 'bg-white/50 group-hover/dot:bg-white/80'"></span>
          </button>
        }
      </div>
    }

    <!-- category badge (pennant) -->
    <div class="absolute top-0 left-4 w-9 h-12 flex items-start justify-center pt-1.5 z-10"
      [style.background]="CATEGORY_META[listing.category].color"
      style="clip-path: polygon(0 0, 100% 0, 100% 100%, 50% 85%, 0 100%);">
      <span aria-hidden="true" class="material-symbols-outlined text-white text-[20px]">{{ CATEGORY_META[listing.category].icon }}</span>
    </div>

    <!-- instant book chip (private listings only) -->
    @if (!('agency' in listing) && listing.reservationProcess === 'instant') {
      <div class="absolute top-3 left-16 z-20 inline-flex items-center gap-1 px-2.5 py-1 bg-trinidad text-white rounded-full shadow-md"
        title="Instant Book — confirms automatically when dates are available">
        <span aria-hidden="true" class="material-symbols-outlined text-[14px]" style="font-variation-settings: 'FILL' 1;">bolt</span>
        <span class="text-[0.6rem] font-label uppercase tracking-[0.1em] font-bold">Instant Book</span>
      </div>
    }
    <!-- boondocking agency chip -->
    @if ('agency' in listing) {
      <div class="absolute top-3 left-16 z-20 inline-flex items-center gap-1 px-2.5 py-1 bg-jungle-green text-white rounded-full shadow-md"
        title="Boondocking — managed by a public agency, first-come-first-served">
        <span aria-hidden="true" class="material-symbols-outlined text-[14px]" style="font-variation-settings: 'FILL' 1;">landscape</span>
        <span class="text-[0.6rem] font-label uppercase tracking-[0.1em] font-bold">{{ listing.agency || 'Boondocking' }}</span>
      </div>
    }

    <!-- favorite heart -->
    <button type="button"
      (click)="onFavoriteClick($event)"
      [attr.aria-label]="isFavorite ? 'Remove from favorites' : 'Save to favorites'"
      [attr.aria-pressed]="isFavorite"
      [class.heart-burst]="justFavorited"
      class="favorite-btn absolute top-3 right-3 z-20 w-11 h-11 rounded-full bg-white/95 backdrop-blur-sm shadow-lg ring-1 ring-black/5 flex items-center justify-center hover:scale-110 transition-transform focus-visible:outline focus-visible:outline-2 focus-visible:outline-trinidad focus-visible:outline-offset-2">
      <span aria-hidden="true" class="material-symbols-outlined text-[20px]"
        [class.text-trinidad]="isFavorite"
        [class.text-dark-text]="!isFavorite"
        [style.font-variation-settings]="isFavorite ? &quot;'FILL' 1&quot; : &quot;'FILL' 0&quot;">
        favorite
      </span>
    </button>

    <!-- P39/B1 — Recently booked social-proof pill. Shows only when
         the listing has booked >= 2 times in the last 30 days.
         TODO: effectively dead — bookings30d is always undefined because
         the upstream mock generator is disabled. Needs a real booking
         count from the backend before this will ever render. -->
    @if (bookings30d && bookings30d >= 2) {
      <div class="absolute top-3 left-3 z-10 inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-dark-text/85 text-white shadow-md">
        <span aria-hidden="true" class="material-symbols-outlined text-[14px]" style="font-variation-settings: 'FILL' 1;">trending_up</span>
        <span class="text-[0.6rem] font-label uppercase tracking-[0.1em] font-bold">Booked {{ bookings30d }}× this month</span>
      </div>
    }

    <!-- merchandising badge (New / Best Value / Guest Favorite) — at most one per card -->
    @if (merchBadge) {
      <div class="absolute bottom-3 left-3 z-10 inline-flex items-center gap-1 px-2.5 py-1 rounded-full shadow-md"
        [ngClass]="merchBadge.bg + ' ' + merchBadge.fg">
        <span aria-hidden="true" class="material-symbols-outlined text-[14px]" style="font-variation-settings: 'FILL' 1;">{{ merchBadge.icon }}</span>
        <span class="text-[0.6rem] font-label uppercase tracking-[0.1em] font-bold">{{ merchBadge.label }}</span>
      </div>
    }

    <!-- price chip — always shows total for selected window when nights > 0
         (mobile has no hover), with the per-night rate underneath. -->
    <div class="absolute bottom-3 right-3 z-10 flex flex-col items-end gap-1.5">
      @if (hoverTotal !== null && !('agency' in listing)) {
        <div class="bg-white/95 backdrop-blur-sm rounded-2xl px-3 py-1.5 shadow-md text-right">
          <div>
            <span class="text-trinidad font-button font-bold text-sm">{{ hoverTotal | currency: 'USD' : 'symbol' : '1.0-0' }}</span>
            <span class="text-dark-text text-[0.65rem] font-label uppercase tracking-[0.1em] ml-1">total · {{ nights }} {{ nights === 1 ? 'night' : 'nights' }}</span>
          </div>
          <div class="text-[0.6rem] text-muted-text font-body">
            {{ listingPrice | currency: 'USD' : 'symbol' : '1.0-0' }} / night · {{ taxTotal !== null ? 'incl. taxes' : 'before taxes' }}
          </div>
        </div>
      } @else if (!('agency' in listing)) {
        <!-- "from": this is the listing's FULL nightly rate, while the detail
             page headlines the cheapest night in the next 90 days. Both are
             real guest prices on different bases, and an unqualified figure
             here read as a contradiction with the lower one on the next page
             (Batch A #1). The detail header already says "from"; this now
             matches it. -->
        <div class="bg-white/95 backdrop-blur-sm rounded-full px-3 py-1.5 shadow-md">
          <span class="text-dark-text text-[0.6rem] font-label uppercase tracking-[0.1em] mr-1">from</span>
          <span class="text-trinidad font-button font-bold text-sm">{{ listing.price | currency: 'USD' : 'symbol' : '1.0-0' }}</span>
          <span class="text-dark-text text-[0.65rem] font-label uppercase tracking-[0.1em] ml-1">/ night</span>
        </div>
      }
    </div>
  </div>

  <div class="p-3.5 flex flex-col gap-2 flex-1">
    <div>
      <h3 class="font-headline font-bold text-dark-text text-base leading-tight line-clamp-1">
        <a [routerLink]="slugRoute" [queryParams]="slugQueryParams"
          class="text-dark-text no-underline after:absolute after:inset-0 after:rounded-2xl focus-visible:outline-none focus-visible:after:outline focus-visible:after:outline-2 focus-visible:after:outline-trinidad focus-visible:after:-outline-offset-2">{{ listing.title }}</a>
      </h3>
      <div class="flex items-center gap-2 mt-0.5 min-w-0">
        <span class="text-muted-text text-xs font-label uppercase tracking-[0.1em] truncate">{{ listing.location }}</span>
        @if (distanceLabel) {
          <span class="text-muted-text text-xs" aria-hidden="true">•</span>
          <span class="text-muted-text text-xs font-label uppercase tracking-[0.1em] shrink-0">{{ distanceLabel }}</span>
        }
        @if (!('agency' in listing) && aggregatedRating.count > 0) {
          <span class="text-muted-text text-xs" aria-hidden="true">•</span>
          <span class="flex items-center gap-1 text-xs text-dark-text font-bold shrink-0">
            <span aria-hidden="true" class="material-symbols-outlined text-sm text-trinidad" style="font-variation-settings: 'FILL' 1;">star</span>
            {{ aggregatedRating.rating }}
            <span class="text-muted-text font-normal">({{ aggregatedRating.count }})</span>
          </span>
        }
        @if (showMapButton) {
          <!-- P39/A4 — Show on map. Phones/tablets only: at lg+ the split map
               already highlights this listing's pin on hover. -->
          <button type="button"
            (click)="onViewOnMapClick($event)"
            aria-label="Show on map"
            title="Show on map"
            class="relative lg:hidden ml-auto shrink-0 w-8 h-8 -my-1 rounded-full inline-flex items-center justify-center text-dark-text hover:bg-cream hover:text-trinidad transition-colors">
            <span aria-hidden="true" class="material-symbols-outlined text-[18px]">location_on</span>
          </button>
        }
      </div>
    </div>

    <!-- One chip row: rig fit, free cancel, 2 amenities, +N, add-ons. -->
    <div class="flex flex-wrap gap-1.5">
      @if (rvFit) {
        <span class="inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-[0.6rem] font-label uppercase tracking-[0.1em] font-bold"
          [ngClass]="rvFit.passes ? 'bg-jungle-green/10 text-jungle-green' : 'bg-trinidad/10 text-trinidad'"
          [title]="rvFit.label">
          <span aria-hidden="true" class="material-symbols-outlined text-[12px]" style="font-variation-settings: 'FILL' 1;">{{ rvFit.passes ? 'check_circle' : 'cancel' }}</span>
          {{ rvFit.label }}
        </span>
      }
      <!-- Guest-favorable cancellation only (easy-goin' + moderate). -->
      @if (goodCancellationChip) {
        <span class="inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-[0.6rem] font-label uppercase tracking-[0.1em] font-bold bg-jungle-green/10 text-jungle-green"
          [title]="goodCancellationChip.summary">
          <span aria-hidden="true" class="material-symbols-outlined text-[12px]" style="font-variation-settings: 'FILL' 1;">event_available</span>
          Free cancel · {{ goodCancellationChip.label }}
        </span>
      }
      @for (a of listing.amenities.slice(0, AMENITY_CHIP_LIMIT); track a) {
        <span class="px-2 py-0.5 bg-jungle-green/10 text-jungle-green text-[0.6rem] font-label uppercase tracking-[0.1em] font-bold rounded-md">
          {{ AMENITY_LABELS[a] }}
        </span>
      }
      @if (listing.amenities.length > AMENITY_CHIP_LIMIT) {
        <!-- P45/B — routes to the listing's #amenities anchor; tooltip lists the rest.
             ::before extends the tap target to 44px without growing the visible chip. -->
        <a [routerLink]="slugRoute" [queryParams]="slugQueryParams"
          fragment="amenities"
          (click)="$event.stopPropagation()"
          [title]="remainingAmenityLabels"
          [attr.aria-label]="(listing.amenities.length - AMENITY_CHIP_LIMIT) + ' more amenities'"
          class="relative px-2 py-0.5 before:absolute before:inset-x-0 before:-inset-y-[13px] before:content-[''] bg-dark-text/10 hover:bg-dark-text/20 text-dark-text text-[0.6rem] font-label uppercase tracking-[0.1em] font-bold rounded-md no-underline transition-colors">
          +{{ listing.amenities.length - AMENITY_CHIP_LIMIT }}
        </a>
      }
      @if (addOnCount > 0) {
        <span class="inline-flex items-center gap-1 px-2 py-0.5 bg-trinidad/10 text-trinidad text-[0.6rem] font-label uppercase tracking-[0.1em] font-bold rounded-md"
          [title]="addOnCount + ' optional add-on' + (addOnCount === 1 ? '' : 's') + ' available'">
          <span aria-hidden="true" class="material-symbols-outlined text-[12px]">add_circle</span>
          {{ addOnCount }} add-on{{ addOnCount === 1 ? '' : 's' }}
        </span>
      }
    </div>

    @if (showAddToTrip) {
      <!-- Primary action is reserving; Add to trip is the secondary icon. Both are
           positioned so they sit above the title's stretched link. -->
      <div class="relative mt-auto pt-1 flex items-center gap-2">
        @if ('agency' in listing) {
          <a [routerLink]="slugRoute" [queryParams]="slugQueryParams"
            [attr.aria-label]="'View site: ' + listing.title"
            class="flex-1 inline-flex items-center justify-center gap-1.5 min-h-[40px] px-3 rounded-full bg-white border border-jungle-green/40 text-jungle-green text-[0.65rem] uppercase tracking-[0.12em] font-button font-bold no-underline hover:bg-jungle-green/10 transition-colors">
            View site
          </a>
        } @else {
          <a [routerLink]="slugRoute" [queryParams]="slugQueryParams"
            [attr.aria-label]="'Reserve ' + listing.title"
            class="flex-1 inline-flex items-center justify-center gap-1.5 min-h-[40px] px-3 rounded-full bg-trinidad text-white text-[0.65rem] uppercase tracking-[0.12em] font-button font-bold no-underline hover:opacity-90 transition-opacity">
            Reserve
          </a>
        }
        <button type="button" (click)="onAddToTripClick($event)"
          aria-label="Add to trip"
          title="Add to trip"
          class="shrink-0 w-10 h-10 inline-flex items-center justify-center rounded-full bg-white border border-dark-text/15 text-jungle-green hover:border-jungle-green hover:bg-jungle-green/10 transition-colors">
          <span aria-hidden="true" class="material-symbols-outlined text-[20px]">add_road</span>
        </button>
      </div>
    }
  </div>
</article>
